import React, { useState, useEffect, useRef, DragEvent } from 'react';
import { getJobOpeningList } from '../../../../utils/api_service/JobOpenings';

interface NewSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFromFile: (file: File) => Promise<void>;
  onCreateFromText: (text: string) => Promise<void>;
  onCreateFromOpening: (jobOpeningId: string) => Promise<void>;
  loading: boolean;
}

type TabType = 'opening' | 'paste' | 'upload';

export const NewSearchModal: React.FC<NewSearchModalProps> = ({
  isOpen,
  onClose,
  onCreateFromFile,
  onCreateFromText,
  onCreateFromOpening,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('opening');
  
  // Tab 1: Opening
  const [openings, setOpenings] = useState<any[]>([]);
  const [openingsLoading, setOpeningsLoading] = useState(false);
  const [openingSearchQuery, setOpeningSearchQuery] = useState('');
  const [selectedOpeningId, setSelectedOpeningId] = useState<string | null>(null);

  // Tab 2: Paste
  const [jdText, setJdText] = useState('');

  // Tab 3: Upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && activeTab === 'opening' && openings.length === 0) {
      fetchOpenings();
    }
  }, [isOpen, activeTab]);

  const fetchOpenings = async () => {
    setOpeningsLoading(true);
    try {
      const res = await getJobOpeningList({} as any, 1, 50);
      if (res && res.json) {
        const data = await res.json();
        const items = data.data || data.openings || data.jobOpenings || data;
        setOpenings(Array.isArray(items) ? items : []);
      }
    } catch (error) {
      console.error('Failed to fetch job openings', error);
    } finally {
      setOpeningsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (activeTab === 'opening' && selectedOpeningId) {
        await onCreateFromOpening(selectedOpeningId);
      } else if (activeTab === 'paste' && jdText.trim()) {
        await onCreateFromText(jdText);
      } else if (activeTab === 'upload' && selectedFile) {
        await onCreateFromFile(selectedFile);
      }
    } catch (err) {
      console.error('Failed to create search', err);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.pdf') || file.name.endsWith('.docx')) {
        setSelectedFile(file);
      } else {
        alert('Please upload a .pdf or .docx file');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  if (!isOpen) return null;

  const filteredOpenings = openings.filter((op: any) =>
    (op.title || op.name || op.jobTitle || '').toLowerCase().includes(openingSearchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-slate-200/80">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-4 flex justify-between items-center text-white">
          <div>
            <h2 className="text-base font-black tracking-tight text-white">Create New Search</h2>
            <p className="text-xs text-slate-400 font-medium">Extract requirements and search your candidate pool</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1.5 rounded-full hover:bg-slate-800"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-6 pt-3 gap-6 bg-slate-50">
          {[
            {
              id: 'opening',
              label: 'From Opening',
              icon: (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              id: 'paste',
              label: 'Paste JD',
              icon: (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              ),
            },
            {
              id: 'upload',
              label: 'Upload JD',
              icon: (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              ),
            },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 pb-3 px-1 text-xs font-bold transition-all border-b-2 ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <span className={isActive ? 'text-indigo-600' : 'text-slate-400'}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-6 flex-1 min-h-[300px] flex flex-col">
          {activeTab === 'opening' && (
            <div className="flex flex-col h-full space-y-3">
              <div className="relative">
                <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search openings (including closed)..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={openingSearchQuery}
                  onChange={(e) => setOpeningSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="flex-1 overflow-y-auto border border-slate-200 rounded-xl bg-slate-50/50 max-h-[260px]">
                {openingsLoading ? (
                  <div className="flex items-center justify-center h-48 text-slate-400">
                    <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : filteredOpenings.length === 0 ? (
                  <div className="flex items-center justify-center h-48 text-xs text-slate-400">
                    No openings found.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredOpenings.map((op: any) => {
                      const id = op._id || op.id;
                      const isSelected = selectedOpeningId === id;
                      return (
                        <div
                          key={id}
                          onClick={() => setSelectedOpeningId(id)}
                          className={`p-3 cursor-pointer transition-all flex justify-between items-center ${
                            isSelected
                              ? 'bg-indigo-50/80 border-l-4 border-indigo-600'
                              : 'hover:bg-slate-100/50'
                          }`}
                        >
                          <div className="flex flex-col">
                            <span className={`text-xs font-bold ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>
                              {op.title || op.name || op.jobTitle || 'Untitled Opening'}
                            </span>
                            {op.jobReferenceNumber && (
                              <span className="text-[10px] text-slate-400 font-mono">
                                #{op.jobReferenceNumber}
                              </span>
                            )}
                          </div>
                          {op.status && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-700">
                              {op.status}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'paste' && (
            <div className="flex flex-col h-full">
              <textarea
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                placeholder="Paste the job description text here..."
                className="w-full flex-1 h-[260px] p-4 border border-slate-200 rounded-xl bg-slate-50/50 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none leading-relaxed"
              />
            </div>
          )}

          {activeTab === 'upload' && (
            <div className="flex flex-col h-full items-center justify-center">
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`w-full p-8 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center cursor-pointer transition-all ${
                  isDragging
                    ? 'border-indigo-500 bg-indigo-50/50'
                    : 'border-slate-300 hover:border-indigo-400 bg-slate-50/50 hover:bg-slate-50'
                }`}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.docx"
                  className="hidden"
                />
                <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3 text-indigo-600">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <p className="text-xs font-bold text-slate-800 mb-1">
                  Click or drag JD file here
                </p>
                <p className="text-[11px] text-slate-400 font-medium">
                  Supports PDF and DOCX
                </p>
              </div>
              
              {selectedFile && (
                <div className="mt-4 flex items-center gap-3 bg-indigo-50/70 px-4 py-2.5 rounded-xl border border-indigo-200/70 w-full">
                  <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span className="text-xs text-indigo-950 font-bold truncate flex-1">
                    {selectedFile.name}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="text-indigo-400 hover:text-indigo-700 p-1 rounded"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/60 flex justify-end gap-2.5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-200/70 rounded-xl transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={
              loading ||
              (activeTab === 'opening' && !selectedOpeningId) ||
              (activeTab === 'paste' && !jdText.trim()) ||
              (activeTab === 'upload' && !selectedFile)
            }
            className="flex items-center gap-2 px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl shadow-md shadow-indigo-200 transition-all"
          >
            {loading && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
            {loading ? 'Processing...' : 'Create Search'}
          </button>
        </div>
      </div>
    </div>
  );
};
